/**
 * Iframe overlay + postMessage bridge — T-311.
 *
 * Lazily injects an iframe pointing at `muse-noir.web.app/index.html`
 * (same Firebase Hosting site as the loader.js itself). On
 * `muse:close` from the iframe, the overlay unmounts entirely (no
 * `display:none` persistence — fresh sessions only per spec).
 *
 * postMessage event allow-list:
 *   - muse:ready       — iframe React tree mounted, inner gate passed
 *   - muse:close       — user dismissed the widget
 *   - muse:terminated  — session ended (success or error)
 *
 * `muse:checkout` is INTENTIONALLY NOT handled here. T-307's
 * ShopifyRedirectAdapter calls window.open synchronously inside the
 * widget's own click handler — no postMessage round-trip needed,
 * no popup-blocker risk.
 */

const SCROLL_LOCK_CLASS = "muse-noir-scroll-locked";

let overlay: HTMLDivElement | null = null;
let iframe: HTMLIFrameElement | null = null;
let bridgeAttached = false;
let messageHandler: ((e: MessageEvent) => void) | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;
let savedScrollY = 0;
let onCloseCallback: (() => void) | null = null;

interface OpenIframeOptions {
  iframeSrc: string;
  cdnOrigin: string;
  onClose: () => void;
}

export function openIframe({
  iframeSrc,
  cdnOrigin,
  onClose,
}: OpenIframeOptions): void {
  if (overlay) return; // already open

  onCloseCallback = onClose;

  // Lock host page scroll without losing position.
  savedScrollY = window.scrollY;
  document.documentElement.style.setProperty(
    "--muse-noir-scroll-y",
    `${savedScrollY}px`,
  );
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  document.body.style.overflow = "hidden";

  overlay = document.createElement("div");
  overlay.id = "muse-noir-overlay";

  const bg = document.createElement("div");
  bg.id = "muse-noir-overlay-bg";
  bg.addEventListener("click", closeIframe);

  iframe = document.createElement("iframe");
  iframe.id = "muse-noir-iframe";
  iframe.src = iframeSrc;
  iframe.allow = "camera; microphone; autoplay";
  iframe.setAttribute("aria-label", "Muse virtual try-on");

  const close = document.createElement("button");
  close.id = "muse-noir-close";
  close.type = "button";
  close.setAttribute("aria-label", "Close virtual try-on");
  close.innerHTML = "&#x2715;";
  close.addEventListener("click", closeIframe);

  overlay.appendChild(bg);
  overlay.appendChild(iframe);
  overlay.appendChild(close);
  document.body.appendChild(overlay);

  if (!bridgeAttached) {
    messageHandler = (event: MessageEvent) => {
      // Origin allow-list: only accept messages from the CDN origin
      // (where the iframe was loaded from).
      if (event.origin !== cdnOrigin) return;
      const data = event.data as { type?: string } | null;
      if (!data || typeof data.type !== "string") return;
      switch (data.type) {
        case "muse:close":
        case "muse:terminated":
          closeIframe();
          break;
        case "muse:ready":
          // Iframe loaded successfully; could fire telemetry here.
          // No-op for v1.
          break;
        default:
          // Unknown event type — ignore. Allow-list policy.
          break;
      }
    };
    window.addEventListener("message", messageHandler);

    escHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && overlay) {
        closeIframe();
      }
    };
    document.addEventListener("keydown", escHandler);

    bridgeAttached = true;
  }
}

export function closeIframe(): void {
  if (!overlay) return;

  overlay.remove();
  overlay = null;
  iframe = null;

  // Detach bridge so a fresh open creates a clean state. Per spec:
  // sessions terminate on close. No display:none persistence.
  if (messageHandler) {
    window.removeEventListener("message", messageHandler);
    messageHandler = null;
  }
  if (escHandler) {
    document.removeEventListener("keydown", escHandler);
    escHandler = null;
  }
  bridgeAttached = false;

  // Restore scroll.
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  document.body.style.overflow = "";
  window.scrollTo(0, savedScrollY);

  // Notify the orchestrator (loader index.ts) so it can re-show the
  // launcher pill.
  const cb = onCloseCallback;
  onCloseCallback = null;
  if (cb) cb();
}
